import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { matchingApi } from '../../api/services';
import { Spinner } from '../Feedback/Spinner';
import { cn } from '../../lib/cn';
import type { Currency, MatchingOptions, MatchingProfile } from '../../types';

interface ProfileWizardProps {
  onComplete: (profile: MatchingProfile, bonusAwarded: number) => void;
  onSkip?: () => void;
  initialProfile?: MatchingProfile;
}

type CountedField = { field: string; count: number };
type CountedCountry = { country: string; count: number };

const STEPS = ['focus', 'destination', 'costs', 'timeline', 'profile'];
const PAGE_SIZE = 18;
const EUR_RATES: Record<Currency, number> = {
  EUR: 1,
  USD: 0.92,
  CAD: 0.68,
  AUD: 0.61,
  GBP: 1.17,
  CHF: 1.05,
  SEK: 0.089,
  NOK: 0.087,
  DKK: 0.134,
  JPY: 0.006,
  CNY: 0.127,
  IRR: 0.000022,
};

const numericValue = (value: string) => (value === '' ? undefined : Number(value));

const formatCurrency = (amount: number, currency: Currency) => {
  if (amount >= 999999) return 'No limit';
  const rounded = amount >= 1000 ? Math.round(amount / 100) * 100 : Math.round(amount);
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(rounded);
};

const convertFromEur = (amount: number, currency: Currency) => {
  if (amount >= 999999) return amount;
  return Math.round(amount / EUR_RATES[currency]);
};

export const ProfileWizard: React.FC<ProfileWizardProps> = ({ onComplete, onSkip, initialProfile }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [options, setOptions] = useState<MatchingOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedFields, setSelectedFields] = useState<string[]>(initialProfile?.preferred_fields || []);
  const [selectedCountries, setSelectedCountries] = useState<string[]>(initialProfile?.preferred_countries || []);
  const [countryFlexible, setCountryFlexible] = useState((initialProfile?.preferred_countries || []).length === 0);
  const [budgetRange, setBudgetRange] = useState<string>('medium');
  const [budgetCurrency, setBudgetCurrency] = useState<Currency>(initialProfile?.budget_currency || 'EUR');
  const [preferScholarships, setPreferScholarships] = useState(initialProfile?.prefer_scholarships || false);
  const [degreeLevel, setDegreeLevel] = useState(initialProfile?.preferred_degree_level || 'MASTER');
  const [targetIntake, setTargetIntake] = useState(initialProfile?.target_intake || '');
  const [languagePreference, setLanguagePreference] = useState(initialProfile?.language_preference || 'english');
  const [ieltsScore, setIeltsScore] = useState<number | undefined>(initialProfile?.ielts_score);
  const [toeflScore, setToeflScore] = useState<number | undefined>(initialProfile?.toefl_score);
  const [greScore, setGreScore] = useState<number | undefined>(initialProfile?.gre_score);
  const [gmatScore, setGmatScore] = useState<number | undefined>(initialProfile?.gmat_score);
  const [gpa, setGpa] = useState<number | undefined>(initialProfile?.gpa);
  const [fieldQuery, setFieldQuery] = useState('');
  const [countryQuery, setCountryQuery] = useState('');
  const [showAllFields, setShowAllFields] = useState(false);
  const [showAllCountries, setShowAllCountries] = useState(false);

  useEffect(() => {
    loadOptions();
  }, []);

  const loadOptions = async () => {
    try {
      const data = await matchingApi.getOptions();
      setOptions(data);
      if (!targetIntake && data.intake_options.length > 0) {
        setTargetIntake(data.intake_options[0].value);
      }
    } catch (err) {
      const message = t('wizard.loadError');
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const fieldOptions = useMemo<CountedField[]>(() => {
    if (!options) return [];
    return options.field_options?.length
      ? options.field_options
      : options.fields.map((field) => ({ field, count: 0 }));
  }, [options]);

  const countryOptions = useMemo<CountedCountry[]>(() => {
    if (!options) return [];
    return options.country_options?.length
      ? options.country_options
      : options.countries.map((country) => ({ country, count: 0 }));
  }, [options]);

  const filteredFields = useMemo(() => {
    const query = fieldQuery.trim().toLowerCase();
    if (!query) return fieldOptions;
    return fieldOptions.filter(({ field }) => field.toLowerCase().includes(query));
  }, [fieldOptions, fieldQuery]);

  const filteredCountries = useMemo(() => {
    const query = countryQuery.trim().toLowerCase();
    if (!query) return countryOptions;
    return countryOptions.filter(({ country }) => country.toLowerCase().includes(query));
  }, [countryOptions, countryQuery]);

  const visibleFields = showAllFields ? filteredFields : filteredFields.slice(0, PAGE_SIZE);
  const visibleCountries = showAllCountries ? filteredCountries : filteredCountries.slice(0, PAGE_SIZE);

  const selectedBudget = options?.budget_ranges[budgetRange] || { min: 0, max: 999999, label: '' };
  const convertedBudget = {
    min: convertFromEur(selectedBudget.min, budgetCurrency),
    max: convertFromEur(selectedBudget.max, budgetCurrency),
  };

  const handleFieldToggle = (field: string) => {
    if (selectedFields.includes(field)) {
      setSelectedFields(selectedFields.filter((item) => item !== field));
    } else if (selectedFields.length < 5) {
      setSelectedFields([...selectedFields, field]);
    }
  };

  const handleCountryToggle = (country: string) => {
    setCountryFlexible(false);
    if (selectedCountries.includes(country)) {
      setSelectedCountries(selectedCountries.filter((item) => item !== country));
    } else {
      setSelectedCountries([...selectedCountries, country]);
    }
  };

  const handleFlexibleCountries = () => {
    setCountryFlexible(true);
    setSelectedCountries([]);
  };

  const canProceed = () => {
    switch (step) {
      case 0:
        return selectedFields.length > 0;
      case 1:
        return countryFlexible || selectedCountries.length > 0;
      case 2:
        return Boolean(budgetRange && budgetCurrency);
      case 3:
        return Boolean(degreeLevel && targetIntake);
      case 4:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);

    const profile: MatchingProfile = {
      preferred_fields: selectedFields,
      preferred_countries: countryFlexible ? [] : selectedCountries,
      budget_min: convertedBudget.min,
      budget_max: convertedBudget.max,
      budget_currency: budgetCurrency,
      preferred_degree_level: degreeLevel,
      target_intake: targetIntake,
      language_preference: languagePreference,
      ielts_score: ieltsScore,
      toefl_score: toeflScore,
      gre_score: greScore,
      gmat_score: gmatScore,
      gpa,
      gpa_scale: '20',
      prefer_scholarships: preferScholarships,
    };

    try {
      const result = await matchingApi.saveProfile(profile);
      onComplete(result.profile, result.bonus_awarded);
      setSaving(false);
    } catch (err) {
      const message = t('wizard.saveError');
      setError(message);
      toast.error(message);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Spinner className="h-12 w-12" />
      </div>
    );
  }

  if (!options) {
    return (
      <div className="py-12 text-center">
        <p className="text-status-danger">{t('wizard.loadError')}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6">
        <div className="mb-3 flex items-center justify-between gap-2">
          {STEPS.map((item, index) => (
            <div key={item} className="flex flex-1 items-center">
              <div
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-all',
                  index <= step ? 'bg-brand-primary text-white' : 'bg-elevated text-text-muted'
                )}
              >
                {index + 1}
              </div>
              {index < STEPS.length - 1 && (
                <div className={cn('mx-2 h-1 flex-1 rounded', index < step ? 'bg-brand-primary' : 'bg-elevated')} />
              )}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-5 gap-2 text-center text-xs text-text-muted">
          {STEPS.map((item, index) => (
            <span key={item} className={index === step ? 'font-semibold text-brand-primary' : ''}>
              {t(`wizard.steps.${item}`)}
            </span>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-border bg-surface p-5 shadow-lg sm:p-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.18 }}
            className="min-h-[440px]"
          >
            {step === 0 && (
              <section>
                <h2 className="mb-2 text-2xl font-bold text-text-primary">{t('wizard.fields.title')}</h2>
                <p className="mb-4 text-text-muted">{t('wizard.fields.subtitle')}</p>
                <input
                  value={fieldQuery}
                  onChange={(event) => setFieldQuery(event.target.value)}
                  placeholder={t('wizard.fields.searchPlaceholder')}
                  className="mb-4 w-full rounded-lg border border-border bg-background px-4 py-3 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary"
                />
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {visibleFields.map(({ field, count }) => {
                    const selected = selectedFields.includes(field);
                    return (
                      <motion.button
                        key={field}
                        onClick={() => handleFieldToggle(field)}
                        disabled={!selected && selectedFields.length >= 5}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                        className={cn(
                          'min-h-[68px] rounded-lg border p-3 text-left transition-all',
                          selected
                            ? 'border-brand-primary bg-brand-primary text-white shadow-md'
                            : selectedFields.length >= 5
                            ? 'border-border bg-elevated text-text-muted'
                            : 'border-border bg-background text-text-secondary hover:border-brand-primary/50'
                        )}
                      >
                        <span className="block text-sm font-semibold leading-5">{field}</span>
                        <span className={cn('mt-1 block text-xs', selected ? 'text-white/80' : 'text-text-muted')}>
                          {count > 0 ? t('wizard.fields.programCount', { count }) : t('wizard.fields.catalogField')}
                        </span>
                      </motion.button>
                    );
                  })}
                </div>
                {filteredFields.length === 0 && (
                  <div className="mt-4 rounded-lg border border-border bg-elevated p-4 text-sm text-text-muted">
                    {t('wizard.fields.noResults')}
                  </div>
                )}
                {filteredFields.length > PAGE_SIZE && (
                  <button onClick={() => setShowAllFields((value) => !value)} className="mt-4 text-sm font-medium text-brand-primary">
                    {showAllFields ? t('wizard.showLess') : t('wizard.showMore')}
                  </button>
                )}
              </section>
            )}

            {step === 1 && (
              <section>
                <h2 className="mb-2 text-2xl font-bold text-text-primary">{t('wizard.countries.title')}</h2>
                <p className="mb-4 text-text-muted">{t('wizard.countries.subtitle')}</p>
                <button
                  onClick={handleFlexibleCountries}
                  className={cn(
                    'mb-4 w-full rounded-lg border p-4 text-left transition-all',
                    countryFlexible ? 'border-brand-primary bg-brand-primary text-white' : 'border-border bg-background text-text-secondary hover:border-brand-primary/50'
                  )}
                >
                  <span className="block font-semibold">{t('wizard.countries.flexibleTitle')}</span>
                  <span className={cn('mt-1 block text-sm', countryFlexible ? 'text-white/80' : 'text-text-muted')}>
                    {t('wizard.countries.flexibleSubtitle')}
                  </span>
                </button>
                <input
                  value={countryQuery}
                  onChange={(event) => setCountryQuery(event.target.value)}
                  placeholder={t('wizard.countries.searchPlaceholder')}
                  className="mb-4 w-full rounded-lg border border-border bg-background px-4 py-3 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary"
                />
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {visibleCountries.map(({ country, count }) => {
                    const selected = selectedCountries.includes(country);
                    return (
                      <motion.button
                        key={country}
                        onClick={() => handleCountryToggle(country)}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                        className={cn(
                          'min-h-[68px] rounded-lg border p-3 text-left transition-all',
                          selected
                            ? 'border-brand-primary bg-brand-primary text-white shadow-md'
                            : 'border-border bg-background text-text-secondary hover:border-brand-primary/50'
                        )}
                      >
                        <span className="block text-sm font-semibold leading-5">{country}</span>
                        <span className={cn('mt-1 block text-xs', selected ? 'text-white/80' : 'text-text-muted')}>
                          {t('wizard.countries.universityCount', { count })}
                        </span>
                      </motion.button>
                    );
                  })}
                </div>
                {filteredCountries.length === 0 && (
                  <div className="mt-4 rounded-lg border border-status-warning/30 bg-status-warning/10 p-4 text-sm text-text-secondary">
                    {t('wizard.countries.noResults')}
                  </div>
                )}
                {filteredCountries.length > PAGE_SIZE && (
                  <button onClick={() => setShowAllCountries((value) => !value)} className="mt-4 text-sm font-medium text-brand-primary">
                    {showAllCountries ? t('wizard.showLess') : t('wizard.showMore')}
                  </button>
                )}
              </section>
            )}

            {step === 2 && (
              <section>
                <h2 className="mb-2 text-2xl font-bold text-text-primary">{t('wizard.budget.title')}</h2>
                <p className="mb-5 text-text-muted">{t('wizard.budget.subtitle')}</p>
                <div className="mb-5 grid gap-3 sm:grid-cols-[1fr_220px]">
                  <label className="block">
                    <span className="mb-2 block text-sm font-medium text-text-secondary">{t('wizard.budget.currency')}</span>
                    <select
                      value={budgetCurrency}
                      onChange={(event) => setBudgetCurrency(event.target.value as Currency)}
                      className="w-full rounded-lg border border-border bg-background p-3 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary"
                    >
                      {(options.budget_currencies || ['EUR']).map((currency) => (
                        <option key={currency} value={currency}>{currency}</option>
                      ))}
                    </select>
                  </label>
                  <label className="flex items-center gap-3 rounded-lg border border-border bg-background p-4">
                    <input
                      type="checkbox"
                      checked={preferScholarships}
                      onChange={(event) => setPreferScholarships(event.target.checked)}
                      className="h-5 w-5 rounded text-brand-primary focus:ring-brand-primary"
                    />
                    <span className="text-sm font-medium text-text-primary">{t('wizard.budget.scholarshipsTitle')}</span>
                  </label>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {Object.entries(options.budget_ranges).map(([key, range]) => {
                    const max = convertFromEur(range.max, budgetCurrency);
                    const min = convertFromEur(range.min, budgetCurrency);
                    const selected = budgetRange === key;
                    const label = key === 'free'
                      ? t('wizard.budget.freeLabel')
                      : key === 'unlimited'
                      ? t('wizard.budget.unlimitedLabel')
                      : `${formatCurrency(min, budgetCurrency)} - ${formatCurrency(max, budgetCurrency)}`;
                    return (
                      <motion.button
                        key={key}
                        onClick={() => setBudgetRange(key)}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                        className={cn(
                          'rounded-lg border p-4 text-left transition-all',
                          selected ? 'border-brand-primary bg-brand-primary text-white shadow-md' : 'border-border bg-background text-text-secondary hover:border-brand-primary/50'
                        )}
                      >
                        <span className="block font-semibold">{label}</span>
                        <span className={cn('mt-1 block text-sm', selected ? 'text-white/80' : 'text-text-muted')}>
                          {t(`wizard.budget.hints.${key}`)}
                        </span>
                      </motion.button>
                    );
                  })}
                </div>
              </section>
            )}

            {step === 3 && (
              <section>
                <h2 className="mb-2 text-2xl font-bold text-text-primary">{t('wizard.timeline.title')}</h2>
                <p className="mb-5 text-text-muted">{t('wizard.timeline.subtitle')}</p>
                <div className="mb-6">
                  <div className="mb-2 text-sm font-medium text-text-secondary">{t('wizard.timeline.degreeLevel')}</div>
                  <div className="grid grid-cols-3 gap-2">
                    {options.degree_levels.map((level) => (
                      <button
                        key={level}
                        onClick={() => setDegreeLevel(level)}
                        className={cn(
                          'rounded-lg border p-4 text-sm font-semibold transition-all',
                          degreeLevel === level ? 'border-brand-primary bg-brand-primary text-white' : 'border-border bg-background text-text-secondary hover:border-brand-primary/50'
                        )}
                      >
                        {t(`degree.${level.toLowerCase()}`)}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="mb-2 text-sm font-medium text-text-secondary">{t('wizard.timeline.intake')}</div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {options.intake_options.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setTargetIntake(option.value)}
                        className={cn(
                          'rounded-lg border p-4 text-left text-sm font-semibold transition-all',
                          targetIntake === option.value ? 'border-brand-primary bg-brand-primary text-white' : 'border-border bg-background text-text-secondary hover:border-brand-primary/50'
                        )}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {step === 4 && (
              <section>
                <h2 className="mb-2 text-2xl font-bold text-text-primary">{t('wizard.extras.title')}</h2>
                <p className="mb-5 text-text-muted">{t('wizard.extras.subtitle')}</p>
                <div className="space-y-5">
                  <div>
                    <div className="mb-2 text-sm font-medium text-text-secondary">{t('wizard.extras.language')}</div>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {options.teaching_languages.map((lang) => (
                        <button
                          key={lang}
                          onClick={() => setLanguagePreference(lang)}
                          className={cn(
                            'rounded-lg border p-3 text-sm font-semibold transition-all',
                            languagePreference === lang ? 'border-brand-primary bg-brand-primary text-white' : 'border-border bg-background text-text-secondary hover:border-brand-primary/50'
                          )}
                        >
                          {t(`language.${lang}`)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-3">
                    <label className="block">
                      <span className="mb-2 block text-sm font-medium text-text-secondary">{t('wizard.extras.gpa')}</span>
                      <div className="flex rounded-lg border border-border bg-background focus-within:border-brand-primary focus-within:ring-2 focus-within:ring-brand-primary">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="20"
                          value={gpa ?? ''}
                          onChange={(event) => setGpa(numericValue(event.target.value))}
                          placeholder="17.25"
                          className="w-full rounded-l-lg bg-transparent p-3 outline-none"
                        />
                        <span className="border-l border-border px-3 py-3 text-sm text-text-muted">/20</span>
                      </div>
                    </label>
                    <label className="block">
                      <span className="mb-2 block text-sm font-medium text-text-secondary">{t('wizard.extras.ielts')}</span>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        max="9"
                        value={ieltsScore ?? ''}
                        onChange={(event) => setIeltsScore(numericValue(event.target.value))}
                        placeholder="6.5"
                        className="w-full rounded-lg border border-border bg-background p-3 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-2 block text-sm font-medium text-text-secondary">{t('wizard.extras.toefl')}</span>
                      <input
                        type="number"
                        min="0"
                        max="120"
                        value={toeflScore ?? ''}
                        onChange={(event) => setToeflScore(numericValue(event.target.value))}
                        placeholder="95"
                        className="w-full rounded-lg border border-border bg-background p-3 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary"
                      />
                    </label>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="block">
                      <span className="mb-2 block text-sm font-medium text-text-secondary">{t('wizard.extras.gre')}</span>
                      <input
                        type="number"
                        min="260"
                        max="340"
                        value={greScore ?? ''}
                        onChange={(event) => setGreScore(numericValue(event.target.value))}
                        placeholder="315"
                        className="w-full rounded-lg border border-border bg-background p-3 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-2 block text-sm font-medium text-text-secondary">{t('wizard.extras.gmat')}</span>
                      <input
                        type="number"
                        min="200"
                        max="800"
                        value={gmatScore ?? ''}
                        onChange={(event) => setGmatScore(numericValue(event.target.value))}
                        placeholder="650"
                        className="w-full rounded-lg border border-border bg-background p-3 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary"
                      />
                    </label>
                  </div>
                </div>
              </section>
            )}
          </motion.div>
        </AnimatePresence>

        {error && (
          <div className="mt-4 rounded-lg border border-status-danger/30 bg-status-danger/10 p-3 text-sm text-status-danger">
            {error}
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setStep((value) => Math.max(value - 1, 0))}
            disabled={step === 0}
            className={cn('rounded-lg px-5 py-3 font-medium transition-all', step === 0 ? 'cursor-not-allowed text-text-muted' : 'text-text-secondary hover:bg-elevated')}
          >
            {t('wizard.back')}
          </button>
          {onSkip && (
            <button onClick={onSkip} className="text-sm text-text-muted transition-colors hover:text-text-primary">
              {t('wizard.skip')}
            </button>
          )}
        </div>
        <button
          onClick={handleNext}
          disabled={!canProceed() || saving}
          className={cn(
            'rounded-lg px-7 py-3 font-semibold shadow-lg transition-all',
            canProceed() && !saving ? 'bg-brand-primary text-white shadow-brand-primary/25 hover:bg-brand-secondary' : 'cursor-not-allowed bg-elevated text-text-muted'
          )}
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <Spinner className="h-4 w-4 border-white border-t-transparent" />
              {t('wizard.saving')}
            </span>
          ) : step === STEPS.length - 1 ? (
            t('wizard.finish')
          ) : (
            t('wizard.next')
          )}
        </button>
      </div>
    </div>
  );
};

export default ProfileWizard;
